/**
 * 頻道頁面模組
 */

//-----------------------------------------------------------------------
// 載入模組
import * as React from 'react';

import get from 'lodash/get';
import { useRecordContext, usePermissions, Pagination, Labeled, TopToolbar, SelectColumnsButton, FilterButton, ExportButton, useShowController, Toolbar } from 'react-admin';
import { List, Show, Edit, SimpleForm, Datagrid, DatagridConfigurable, SingleFieldList } from 'react-admin';
import { TextField, NumberField, ShowButton, ReferenceField, ReferenceManyField, UrlField, DateField, ReferenceArrayField, ChipField } from 'react-admin';
import { TabbedShowLayout, Tab, TextInput, ReferenceInput, ReferenceArrayInput, SelectInput, AutocompleteArrayInput } from 'react-admin';
import { downloadCSV, DataProvider } from 'react-admin';
import { MediaTypeField, TargetStatusField } from './target';
import { SaveOnlyToolbar, TextUrlField, ListEditButton } from '../lib/components';
import { Grid, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import jsonExport from 'jsonexport/dist';

//-----------------------------------------------------------------------
// defines
import { CHANNEL_STATUS } from '../common/defines';
import { NamePairInfo } from '../common/interfaces';
import { MemberAuthInfo } from '../providers/authProvider';
import { TimeRangeInput } from '../lib/components';

//-----------------------------------------------------------------------
/** 頻道狀態顯示欄位元件。 */

const channelStatusMap = new Map<string, string>();
const channelStatusList : NamePairInfo<string>[] =
[
	{ id: CHANNEL_STATUS.Tracking,	name: "追蹤中" },
	{ id: CHANNEL_STATUS.Untracked,	name: "停止追蹤" }
];

for(let status of channelStatusList)
	channelStatusMap.set(status.id, status.name);

export const ChannelStatusField = (props: any) =>
{
	const { source } = props;
	const record = useRecordContext(props);

	return (<span className="MuiTypography-body2 MuiTypography-root">{channelStatusMap.get(get(record, source))}</span>);
}

ChannelStatusField.defaultProps =
{
	label: '狀態',
	addLabel: true,
};

//-----------------------------------------------------------------------
/** 頻道頁面標題。 */
const ChannelTitle = (props: any) =>
{
	const record = useRecordContext(props);

    return <span>Post {record ? ` #${record.id} ${record.name} ` : ''}</span>;
}

//-----------------------------------------------------------------------
/** 頻道列表過濾器。 */
const channelFilters =
[
	<TextInput key='input-name' label='搜尋名稱' source='name' InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon color="disabled" /></InputAdornment>) }} resettable alwaysOn />,
	<ReferenceInput key='input-source' label='資料來源' source='sourceId' reference='source' resettable alwaysOn >
		<SelectInput optionText='name' />
	</ReferenceInput>,
	<ReferenceArrayInput key='input-region' label='區域' source='regionId' reference='regionAll'>
		<AutocompleteArrayInput optionText='name' />
	</ReferenceArrayInput>,
	<ReferenceInput key='input-keyword' label='關鍵字' source='keywordId' reference='keywordAll' alwaysOn >
		<SelectInput optionText='text' />
	</ReferenceInput>,
	<ReferenceArrayInput key='input-kol' label='KOL' source='kolId' reference='kolAll'>
		<AutocompleteArrayInput optionText='name' />
	</ReferenceArrayInput>,
	<TimeRangeInput key='input-time-range' alwaysOn />,
	<SelectInput key='input-status' label='狀態' source='status' choices={channelStatusList} resettable />
];

const ListActions = () => (
    <TopToolbar>
        <SelectColumnsButton />
        <FilterButton />
        <ExportButton maxResults={100000} />
    </TopToolbar>
);

/** 資料輸出功能。 */
const exporter = (records: any, fetchRelatedRecords: (data: any, field: string, resource: string) => Promise<any>, dataProvider: DataProvider) =>
{
	fetchRelatedRecords(records, 'sourceId', 'source').then((sources) =>
	{
		fetchRelatedRecords(records, 'regionId', 'region').then((regions) =>
		{
			fetchRelatedRecords(records, 'kolId', 'kol').then((kols) =>
			{
				fetchRelatedRecords(records, 'keywordIds', 'keyword').then((keywords) =>
				{
					const data = records.map((record: any) =>
					{
						let	keywordIds = record.keywordIds as number[];
						let keywordList : string[] = [];

						for(let keywordId of keywordIds)
						{
							if(keywords[keywordId])
								keywordList.push(keywords[keywordId].text);
						}

						return {
							...record,
							sourceId: sources[record.sourceId].name,
							regionId: regions[record.regionId].name,
							kolId: kols[record.kolId] ? kols[record.kolId].name : '',
							keywordIds: keywordList
						};
					});

					return jsonExport(data,
					{
						headers	: [ 'id', 'sourceId', 'regionId', 'kolId', 'name', 'url', 'keywordIds', 'targetCount', 'targetIncrease', 'viewCount', 'viewIncrease', 'viewOfNew', 'commentCount', 'commentIncrease', 'commentOfNew', 'status', 'createTime' ],
						rename	: [ 'id', '資料來源', '區域', '所屬 KOL', '頻道名稱', '頻道網址', '關鍵字', '目標數量', '新增目標量', '累計瀏覽量', '瀏覽增加量', '新增目標瀏覽量', '留言數量', '留言增加量', '新增目標留言量', '狀態', '建立時間' ]
					}, (err, csv) =>
					{
						downloadCSV("\ufeff" + csv, 'channel');
					});
				});
			});
		});
	});
};

/** 頻道所屬目標輸出功能。 */
const targetExporter = (records: any, fetchRelatedRecords: (data: any, field: string, resource: string) => Promise<any>, dataProvider: DataProvider) =>
{
	fetchRelatedRecords(records, 'sourceId', 'source').then((sources) =>
	{
		fetchRelatedRecords(records, 'regionId', 'region').then((regions) =>
		{
			fetchRelatedRecords(records, 'channelId', 'channel').then((channels) =>
			{
				fetchRelatedRecords(records, 'keywordIds', 'keyword').then((keywords) =>
				{
					const data = records.map((record: any) =>
					{
						let	keywordIds = record.keywordIds as number[];
						let keywordList : string[] = [];

						for(let keywordId of keywordIds)
						{
							if(keywords[keywordId])
								keywordList.push(keywords[keywordId].text);
						}

						return {
							...record,
							sourceId: sources[record.sourceId].name,
							regionId: regions[record.regionId].name,
							channelId: channels[record.channelId].name,
							keywordIds: keywordList
						};
					});

					return jsonExport(data,
					{
						headers	: [ 'id', 'sourceId', 'regionId', 'channelId', 'keywordIds', 'title', 'url', 'viewCount', 'viewIncrease', 'commentCount', 'commentIncrease', 'status', 'publishTime' ],
						rename	: [ 'id', '資料來源', '區域', '所屬頻道', '關鍵字', '標題', '目標網址', '累計瀏覽量', '瀏覽增加量', '留言數量', '留言增加量', '狀態', '發布日期' ]
					}, (err, csv) =>
					{
						downloadCSV("\ufeff" + csv, 'channelTargets');
					});
				});
			});
		});
	});
};

/** 頻道列表頁面。 */
export const ChannelList = (props: any) =>
{
	let { permissions } = usePermissions();
	let	authInfo = permissions as MemberAuthInfo;

	return <List {...props} title='頻道' sort={{ field: 'viewCount', order: 'DESC' }} filters={channelFilters} actions={<ListActions />} exporter={exporter}>
		<DatagridConfigurable bulkActionButtons={false}>
			<TextField source='id' />
			<ReferenceField label="資料來源" source="sourceId" reference="source">
				<TextField source="name" />
			</ReferenceField>
			<ReferenceField label="區域" source="regionId" reference="region">
				<TextField source="name" />
			</ReferenceField>
			<ReferenceField label="所屬 KOL" source="kolId" reference="kol">
				<TextField source="name" />
			</ReferenceField>
			<TextUrlField source='url' textSource='name' label='頻道連結' target='_blank' sortable={false} />
			<ReferenceArrayField label="關鍵字" source="keywordIds" reference="keyword" sortable={false} >
				<SingleFieldList>
					<ChipField source="text" />
				</SingleFieldList>
			</ReferenceArrayField>
			<NumberField source='targetCount' label='目標數量' />
			<NumberField source='targetIncrease' label='新增目標量' />
			<NumberField source='viewCount' label='累計瀏覽量' />
			<NumberField source='viewIncrease' label='瀏覽增加量' />
			<NumberField source='viewOfNew' label='新增目標瀏覽量' />
			<NumberField source='reactCount' label='累計反應量' />
			<NumberField source='reactIncrease' label='反應增加量' />
			<NumberField source='reactOfNew' label='新增目標反應量' />
			<NumberField source='commentCount' label='留言數量' />
			<NumberField source='commentIncrease' label='留言增加量' />
			<NumberField source='commentOfNew' label='新增目標留言量' />
			<ChannelStatusField source='status' />
			<DateField source='createTime' label='資料加入時間' showTime />
			<ListEditButton edit={authInfo.channelEdit} />
			<ShowButton />
		</DatagridConfigurable>
	</List>;
};

/** 頻道資訊頁面。 */
export const ChannelShow = (props: any) =>
{
	const { record, isLoading } = useShowController();

	if(isLoading)
		return <></>;

	return <Show title={<ChannelTitle />} {...props}>
		<TabbedShowLayout>
			<Tab label='所屬目標'>
				<ReferenceManyField reference='target' target='t_c_id' filter={{ channelId: props.id }} pagination={<Pagination />} sort={{ field: 'viewCount', order: 'DESC' }} >
					<Toolbar>
						<Grid container spacing={2}  >
							<Grid size={11}></Grid>
							<Grid size={1}><ExportButton resource='target' maxResults={100000} exporter={targetExporter} /></Grid>
						</Grid>
					</Toolbar>
					<Datagrid bulkActionButtons={false}>
						<TextField source='id' />
						<ReferenceArrayField label="關鍵字" source="keywordIds" reference="keyword" sortable={false} >
							<SingleFieldList>
								<ChipField source="text" />
							</SingleFieldList>
						</ReferenceArrayField>
						<TextField source='title' label='標題' />
						<UrlField source='url' label='網址' target='_blank' />
						<MediaTypeField source='mediaType' />
						<NumberField source='viewCount' label='瀏覽量' />
						<NumberField source='reactCount' label='反應量' />
						<NumberField source='commentCount' label='留言數量' />
						<TargetStatusField source='status' />
						<DateField source='publishTime' label='發布日期' />
					</Datagrid>
				</ReferenceManyField>
			</Tab>
			<Tab label='總覽'>
				<TextField source='id' />
				<ReferenceField label="資料來源" source="sourceId" reference="source" >
					<TextField source="name" />
				</ReferenceField>
				<ReferenceField label="區域" source="regionId" reference="region">
					<TextField source="name" />
				</ReferenceField>
				<ReferenceField label="所屬 KOL" source="kolId" reference="kol">
					<TextField source="name" />
				</ReferenceField>
				<TextField source='name' label='名稱' />
				<ReferenceArrayField label="符合的關鍵字" source="keywordIds" reference="keyword" >
					<SingleFieldList>
						<ChipField source="text" />
					</SingleFieldList>
				</ReferenceArrayField>
				<UrlField source='url' label='網址' target='_blank' />
				<ChannelStatusField source='status' />
				<DateField source='createTime' label='資料加入時間' showTime />
			</Tab>
		</TabbedShowLayout>
	</Show>
}
/*
const keywordValidator : Validator = (value: any, values: any, props: any) =>
{
	if(Array.isArray(value))
	{
		if(value.length < 1)
			return "不可沒有關鍵字！";
	}

	return undefined;
}
*/
/** 頻道編輯頁面。 */
export const ChannelEdit = (props: any) => (
	<Edit title={<ChannelTitle />} {...props}>
		<SimpleForm toolbar={<SaveOnlyToolbar/>}>
			<Labeled label="ID"><TextField source='id' /></Labeled>
			<Labeled label="資料來源">
				<ReferenceField source="sourceId" reference="source" >
					<TextField source="name" />
				</ReferenceField>
			</Labeled>
			<Labeled label="區域">
				<ReferenceField source="regionId" reference="region">
					<TextField source="name" />
				</ReferenceField>
			</Labeled>
			<Labeled label="所屬 KOL">
				<ReferenceInput source="kolId" reference="kol">
					<SelectInput optionText='name' />
				</ReferenceInput>
			</Labeled>
			<Labeled label="名稱"><TextField source='name' /></Labeled>
			<Labeled label="符合的關鍵字"><ReferenceArrayField source="keywordIds" reference="keyword" >
				<SingleFieldList>
					<ChipField source="text" />
				</SingleFieldList>
			</ReferenceArrayField></Labeled>
			<Labeled label="網址"><UrlField source='url' target='_blank' /></Labeled>
			<SelectInput source='status' choices={channelStatusList} label="狀態" />
			<Labeled label="資料加入時間"><DateField source='createTime' showTime /></Labeled>
		</SimpleForm>
	</Edit>
);
